import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, FileText, Send, Star } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import MarkdownEditor from "@/components/MarkdownEditor";
import TagsInput from "@/components/TagsInput";
import TaxonomyPicker from "@/components/TaxonomyPicker";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  cover_image_url: "",
  body_md: "",
  author_name: "",
  author_role: "",
  author_picture: "",
  domain: "",
  taxonomy_node_id: "",
  tags: [],
  technologies: [],
  status: "draft",
  is_premium: false,
  is_featured: false,
  read_minutes: 5,
  send_to_subscribers: false,
};

export default function AdminBlogsPage() {
  const [items, setItems] = useState([]);
  const pager = usePager(items, 15);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blogs");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (editing === "new") await api.post("/admin/blogs", form);
      else await api.put(`/admin/blogs/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    await api.delete(`/admin/blogs/${id}`);
    toast.success("Deleted");
    load();
  };
  const broadcast = async (id) => {
    if (!window.confirm("Email this post to all opted-in registered users?")) return;
    try {
      const r = await api.post(`/admin/blogs/${id}/broadcast`);
      toast.success(`Sent: ${r.data.sent} · Failed: ${r.data.failed} · Total: ${r.data.total}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to broadcast");
    }
  };

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">BLOG / JOURNAL</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
            Manage <em className="italic text-kb-primary">articles</em>
          </h1>
        </div>
        <button
          onClick={() => {
            setEditing("new");
            setForm(EMPTY);
          }}
          className="kb-btn-primary text-sm"
          data-testid="blog-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New article
        </button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="blog-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">
              {editing === "new" ? "NEW ARTICLE" : "EDITING"}
            </div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            <input
              className="kb-input text-sm w-full"
              placeholder="Title *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="blog-title-input"
            />
            <input
              className="kb-input text-sm w-full"
              placeholder="Slug (auto-generated if blank)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              data-testid="blog-slug-input"
            />
            <textarea
              rows={2}
              className="kb-input text-sm w-full resize-none"
              placeholder="Short excerpt — shown on cards and email preview"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              data-testid="blog-excerpt-input"
            />
            <input
              className="kb-input text-sm w-full"
              placeholder="Cover image URL (16:9 recommended)"
              value={form.cover_image_url}
              onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
            />
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Body (Markdown)</label>
              <MarkdownEditor value={form.body_md} onChange={(v) => setForm({ ...form, body_md: v })} />
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <input
                className="kb-input text-sm"
                placeholder="Author name (defaults to you)"
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              />
              <input
                className="kb-input text-sm"
                placeholder="Author role / title"
                value={form.author_role}
                onChange={(e) => setForm({ ...form, author_role: e.target.value })}
              />
            </div>
            <input
              className="kb-input text-sm w-full"
              placeholder="Author picture URL"
              value={form.author_picture}
              onChange={(e) => setForm({ ...form, author_picture: e.target.value })}
            />

            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Category (taxonomy)</label>
              <TaxonomyPicker
                domain="both"
                value={form.taxonomy_node_id}
                onChange={(id) => setForm({ ...form, taxonomy_node_id: id || "" })}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <input
                className="kb-input text-sm"
                placeholder="Domain (e.g. AI/ML)"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
              />
              <input
                type="number"
                min="1"
                className="kb-input text-sm"
                placeholder="Read minutes"
                value={form.read_minutes}
                onChange={(e) =>
                  setForm({ ...form, read_minutes: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>

            <TagsInput
              value={form.tags || []}
              onChange={(tags) => setForm({ ...form, tags })}
              placeholder="Tags (type and press Enter)"
              testId="blog-tags"
            />
            <TagsInput
              value={form.technologies || []}
              onChange={(arr) => setForm({ ...form, technologies: arr })}
              placeholder="Technologies (type and press Enter)"
              testId="blog-tech"
            />

            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              <select
                className="kb-input text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="blog-status-select"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
                <input
                  type="checkbox"
                  checked={!!form.is_premium}
                  onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
                  data-testid="blog-premium-checkbox"
                />
                Premium gated (preview only for free users)
              </label>
              <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
                <input
                  type="checkbox"
                  checked={!!form.is_featured}
                  onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                  data-testid="blog-featured-checkbox"
                />
                Featured (appears as hero on /blogs)
              </label>
              <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
                <input
                  type="checkbox"
                  checked={!!form.send_to_subscribers}
                  onChange={(e) => setForm({ ...form, send_to_subscribers: e.target.checked })}
                  data-testid="blog-send-checkbox"
                />
                Email to registered users on publish
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="blog-save-btn">
              Save article
            </button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
          No articles yet. Click <strong>New article</strong> to publish your first post.
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((b) => (
            <div key={b.id} className="kb-card p-5" data-testid={`admin-blog-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg text-kb-ink mt-2 leading-snug line-clamp-2">{b.title}</h3>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                    {b.author_name || "—"} · {b.read_minutes || 0}m read
                  </div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider mt-2 text-kb-ink-light flex flex-wrap gap-2">
                    <span className={b.status === "published" ? "text-emerald-600" : "text-amber-600"}>
                      {b.status?.toUpperCase()}
                    </span>
                    {b.is_premium && <span>· PREMIUM</span>}
                    {b.is_featured && (
                      <span className="text-kb-primary inline-flex items-center gap-1">
                        · <Star className="w-3 h-3" /> FEATURED
                      </span>
                    )}
                    {b.broadcast_stats?.sent ? <span>· Mailed to {b.broadcast_stats.sent}</span> : null}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => {
                      setEditing(b.id);
                      setForm({ ...EMPTY, ...b });
                    }}
                    className="p-1.5 text-kb-ink-muted hover:text-kb-primary"
                    data-testid={`admin-blog-${b.id}-edit`}
                    title="Edit"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  {b.status === "published" && (
                    <button
                      onClick={() => broadcast(b.id)}
                      className="p-1.5 text-kb-ink-muted hover:text-kb-primary"
                      data-testid={`admin-blog-${b.id}-broadcast`}
                      title="Send to subscribers now"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => remove(b.id)}
                    className="p-1.5 text-rose-600 hover:text-rose-700"
                    data-testid={`admin-blog-${b.id}-delete`}
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Pager {...pager} label="articles" testId="admin-blogs-pager" />
    </div>
  );
}
